import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {IThemeVariables} from '@toolify/client/src/modules/theme/types/IThemeVariables'
import {
  FiordNightThemeVariables,
} from '@toolify/client/src/modules/theme/strategies/fiord-night/FiordNightThemeVariables'
import {
  getLightModernDerivedVariables,
} from '@toolify/client/src/modules/theme/functions/getLightModernDerivedVariables'

const LightModernBaseThemeVariables: IThemeVariables = {
  ...FiordNightThemeVariables,

  [ThemeVariableType.PrimaryColor]: '#5e6ad2',
  [ThemeVariableType.PrimaryHoverColor]: '#4f5bc4',
  [ThemeVariableType.OnPrimaryColor]: '#ffffff',
  [ThemeVariableType.OnPrimaryHoverColor]: '#ffffff',
  [ThemeVariableType.PrimaryContainerColor]: '#ebedfa',
  [ThemeVariableType.PrimaryContainerHoverColor]: '#dee1f7',
  [ThemeVariableType.OnPrimaryContainerColor]: '#353e8f',

  [ThemeVariableType.SecondaryColor]: '#6b6f7b',
  [ThemeVariableType.OnSecondaryColor]: '#ffffff',
  [ThemeVariableType.SecondaryContainerColor]: '#e2e4ed',
  [ThemeVariableType.OnSecondaryContainerColor]: '#313540',

  [ThemeVariableType.TertiaryColor]: '#9200c4',
  [ThemeVariableType.OnTertiaryColor]: '#ffffff',
  [ThemeVariableType.TertiaryContainerColor]: '#f4e4fe',
  [ThemeVariableType.OnTertiaryContainerColor]: '#560179',

  [ThemeVariableType.ErrorColor]: '#c2362b',
  [ThemeVariableType.OnErrorColor]: '#ffffff',
  [ThemeVariableType.ErrorContainerColor]: '#fde5e2',
  [ThemeVariableType.OnErrorContainerColor]: '#8c1308',

  [ThemeVariableType.SurfaceDimColor]: '#f4f5f8',
  [ThemeVariableType.SurfaceColor]: '#f8f9fb',
  [ThemeVariableType.SurfaceBrightColor]: '#ffffff',

  [ThemeVariableType.ContainerHighestColor]: '#cfd3e0',
  [ThemeVariableType.ContainerHighColor]: '#dde0ea',
  [ThemeVariableType.ContainerColor]: '#f2f3f7',
  [ThemeVariableType.ContainerLowColor]: '#ffffff',
  [ThemeVariableType.ContainerLowestColor]: '#eef0f5',

  [ThemeVariableType.ContentBrightColor]: '#21232c',
  [ThemeVariableType.ContentLightColor]: '#2b2e38',
  [ThemeVariableType.ContentNormalColor]: '#313540',
  [ThemeVariableType.ContentDimColor]: '#6b6f7b',
  [ThemeVariableType.ContentDarkColor]: '#989da9',
  [ThemeVariableType.ContentDarkestColor]: '#c4c8d1',

  [ThemeVariableType.BorderRadius]: 8,
  [ThemeVariableType.Shadow]: '#1d283a1a',

  [ThemeVariableType.DatabaseTableGridColor]: '#e4e6ed',
  // lekki tekst komorek: 500 - 100 = 400
  [ThemeVariableType.DatabaseTableFontWeightOffset]: -100,
  [ThemeVariableType.DatabaseTableNameFontWeightOffset]: 100,
  // nazwy kolumn w naglowku jak Name: 500 + 100 - 100 = 500
  [ThemeVariableType.DatabaseTableColumnHeaderFontWeightOffset]: 100,
  [ThemeVariableType.DatabaseTableHeaderDefaultBackgroundColor]: '#ffffff',
  [ThemeVariableType.DatabaseTableHeaderHoverBackgroundColor]: '#f2f3f7',
  [ThemeVariableType.DatabaseTableStickyShadowColor]: 'rgba(40, 44, 70, 0.04)',
  [ThemeVariableType.DatabaseTableScrollShadowColor]: 'rgba(40, 44, 70, 0.04)',
  // = DatabaseTableGridColor; useThemeVariables przepisuje go z siatki wybranej palety
  [ThemeVariableType.DatabaseTableCardBorderColor]: '#ecedee',
  // Geometria jak w monday.com: --board-elevation (0 8px 8px -8px), plywajacy naglowek grupy (4px 6px 15px -8px) i przypieta kolumna nazwy (2px 0 8px -6px). Kolor = atrament palety
  [ThemeVariableType.DatabaseTableGroupElevation]: '0 0.5rem 0.5rem -0.5rem rgba(29, 40, 58, 0.1)',
  [ThemeVariableType.DatabaseTableStickyHeaderElevation]: '0.25rem 0.375rem 0.9375rem -0.5rem rgba(29, 40, 58, 0.26)',
  [ThemeVariableType.DatabaseTablePinnedColumnElevation]: '0.125rem 0 0.5rem -0.375rem rgba(29, 40, 58, 1)',
  [ThemeVariableType.UseDatabaseTableCardLayout]: true,
  [ThemeVariableType.DatabaseCellHoverBackgroundColor]: '#f2f3f7',
  // hover i fokus komorki 1px; edycja zostaje przy 2px (CELL_BORDER_WIDTH) - zmiane trybu widac po grubosci obrysu
  [ThemeVariableType.DatabaseCellHoverBorder]: '0.0625rem solid var(--container-highest-color)',
  [ThemeVariableType.DatabaseCellFocusHoverBorder]: '0.0625rem solid var(--primary-hover-color)',

  // ikona relacji w komorce tabeli: Fiord Night ma tu granat #222B3A/#2C3747 - w light tlo jak hover komorki
  [ThemeVariableType.DatabaseRelationIconBackgroundColor]: '#f2f3f7',
  [ThemeVariableType.DatabaseRelationIconBackgroundHoverColor]: '#dde0ea',

  [ThemeVariableType.UseScrollbarGradients]: false,
  [ThemeVariableType.UseLightBadgeColors]: true,

  // T-946: kolory z JS dobrane dotad do ciemnego tla. Akcent = primary palety (nadpisania palet w
  // LightModernVariantOverridesMap), mapa = jasny styl 2D, naglowki ModalHeaderComponent bez tla
  // (bialy naglowek nad bialym menu byl niewidoczny, a szary pas w Filters - przypadkowy; podzial robi
  // obrys --divider-color po stronie komponentu)
  [ThemeVariableType.CalendarEventDefaultColor]: '#5e6ad2',
  [ThemeVariableType.MapboxStreetStyleUrl]: 'mapbox://styles/hypris/cmls6zrpo000l01qz2net6jl9',
  [ThemeVariableType.ShouldShowModalHeaderComponentBackground]: false,
  [ThemeVariableType.TimeTrackerChartBarColor]: '#5e6ad2',
  [ThemeVariableType.TimeTrackerChartBarBorderColor]: '#ffffff',
}

// Zmienne pochodne (wykresy, kanwa automatyzacji, tablica) dla bazowej palety (Lavenda); useThemeVariables liczy je
// od nowa po nadpisaniach palety i atramentu
export const LightModernThemeVariables: IThemeVariables = {
  ...LightModernBaseThemeVariables,
  ...getLightModernDerivedVariables(LightModernBaseThemeVariables),
}
